import GridContainer from "@/components/common/GridContainer"
import styled from "styled-components"
import {
	getCssSizeValue,
	getHtmlFromRedactorField,
	getIdWithoutSpecialSymbols,
	getSeparatePaddingClasses,
	isStringEmpty,
} from "@/utils/utils"
import Button, { getButtonLinkData } from "@/components/common/Buttons/Button"
import UniversalLink from "@/components/common/Links/UniversalLink"
import { ICONS, PAGES } from "@/constants"
import usePreviousRoute from "@/hooks/usePreviousRoute"
import { BREAKPOINTS, colors } from "@/styles/themeSettings"
import { useRef } from "react"
import Canvas from "@/components/BannerCallToActionModule/Canvas"
import HeaderDescriptionBlock from "@/components/HeaderDescriptionBlock"
import SimpleIconButton, { SIMPLE_ICON_BUTTON_CLASS_NAME, SIMPLE_ICON_BUTTON_VARIANTS } from "@/components/common/SimpleIconButton"
import { useIsDesktop } from "@/hooks/useDevice"

export const VARIANTS = {
	RED: "red",
	NAVY_BLUE: "navy-blue",
	RED_WHITE_CROSSES: "red-white-crosses",
}

const Wrapper = styled.section`
	position: relative;

	.${SIMPLE_ICON_BUTTON_CLASS_NAME} {
		position: absolute;
		right: 0;
		top: 0;
		z-index: 2;
	}
`

const ContentContainer = styled.section`
	position: relative;
	--supp-color: ${(props) => props.theme.colors.white};
	--accent-color: ${(props) => props.theme.colors.richBlack};
	color: var(--supp-color);
	//background: transparent;
	padding-top: ${getCssSizeValue(48, BREAKPOINTS.MOBILE)};
	background-color: ${(props) => props.theme.colors.chilliRed};

	&.variant-${VARIANTS.NAVY_BLUE} {
		background-color: ${(props) => props.theme.colors.richBlue};
		--supp-color: ${(props) => props.theme.colors.white};
		--accent-color: ${(props) => props.theme.colors.periwinkle};
	}

	&.variant-${VARIANTS.RED_WHITE_CROSSES} {
		background-color: ${(props) => props.theme.colors.chilliRed};
		--supp-color: ${(props) => props.theme.colors.white};
		--accent-color: ${(props) => props.theme.colors.richBlack};
	}

	.first-row {
		pointer-events: none;

		.text {
			position: relative;
			z-index: 1;
			width: fit-content;
			border-bottom: 5px solid var(--accent-color);
			padding-bottom: 5px;
		}
	}

	.line {
		background: var(--supp-color);
		height: 1px;
		translate: 0 -3px;
	}

	.second-row {
		pointer-events: none;
		position: relative;
		margin-top: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		.first-row {
			.text {
				padding-bottom: unset;
			}
		}

		.second-row {
			margin-top: ${getCssSizeValue(24, BREAKPOINTS.TABLET)};
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding-top: ${getCssSizeValue(96)};
		.second-row {
			margin-top: ${getCssSizeValue(32, BREAKPOINTS.DESKTOP)};
		}
	}
`

const StyledButtonWrapper = styled(GridContainer)`
	margin-top: ${getCssSizeValue(32, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		margin-top: ${getCssSizeValue(20, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-top: ${getCssSizeValue(32, BREAKPOINTS.DESKTOP)};
	}
`

const BG_COLORS = {
	[VARIANTS.RED]: {
		value: colors.chilliRed,
	},
	[VARIANTS.NAVY_BLUE]: {
		value: colors.richBlue,
	},
	[VARIANTS.RED_WHITE_CROSSES]: {
		value: colors.chilliRed,
	},
}

function getFinalHref(button, paymentId) {
	const { url, externalLink, slug } = button

	if (!isStringEmpty(paymentId) && slug === PAGES.I_PAY) {
		return `/${PAGES.I_PAY}#${paymentId}`
	}

	if (!isStringEmpty(url)) {
		return url
	}

	if (!isStringEmpty(externalLink)) {
		return externalLink
	}
}

function getButtonColor(variant) {
	switch (variant) {
		case VARIANTS.RED:
			return colors.white
		case VARIANTS.NAVY_BLUE:
			return colors.richBlack
		case VARIANTS.RED_WHITE_CROSSES:
			return colors.white
		default:
			return colors.white
	}
}

export default function BannerCallToActionModule({ data }) {
	const { paddingTop, paddingBottom } = getSeparatePaddingClasses(data)
	const paymentId = getIdWithoutSpecialSymbols(data?.payment?.id || "")
	const header = data?.header || ""
	const description = data?.description || ""
	const variant = data?.variant || VARIANTS.RED
	const bgColor = BG_COLORS[variant]

	const button = getButtonLinkData(data?.button)
	const { newCard, text: buttonText, isExternal } = button
	const finalHref = getFinalHref(button, paymentId)
	const { saveCurrentPath } = usePreviousRoute()
	const isDesktop = useIsDesktop()

	const spawnerRef = useRef()
	const handleAddBall = () => {
		if (spawnerRef.current) {
			spawnerRef.current.addBall()
		}
	}

	return (
		<Wrapper className={data?.headerDescription ? paddingTop : ""}>
			{isDesktop && (
				<SimpleIconButton
					onClick={handleAddBall}
					color={getButtonColor(variant)}
					bgColor={colors.richBlue}
					borderColor={colors.richBlack}
					name={ICONS.CROSS}
					variant={SIMPLE_ICON_BUTTON_VARIANTS.BIG}
					pointer
				/>
			)}
			<HeaderDescriptionBlock headerDescription={data?.headerDescription} />
			<ContentContainer className={`variant-${variant} ${paddingBottom}`}>
				{isDesktop && <Canvas ref={spawnerRef} variant={variant} bgColor={bgColor} />}

				<GridContainer className="indent-desktop-1 first-row">
					<div className="typo-borna typo-borna-57 col-mobile-8 col-tablet-4 col-desktop-11">
						<div className="text no-select" dangerouslySetInnerHTML={getHtmlFromRedactorField(header)} />
					</div>
				</GridContainer>
				<GridContainer className="no-margin">
					<div className="line col-desktop-8 col-tablet-6 col-mobile-8"></div>
				</GridContainer>
				<GridContainer className="indent-desktop-1 second-row">
					<div className="typo-strawford typo-strawford-97 col-mobile-8 col-tablet-6 col-desktop-10">
						<div dangerouslySetInnerHTML={getHtmlFromRedactorField(description)} />
					</div>
				</GridContainer>
				<StyledButtonWrapper className="indent-desktop-1">
					<div className="col-desktop-3">
						<UniversalLink newCard={newCard} external={isExternal} href={finalHref}>
							<Button onClick={saveCurrentPath} variant="secondary">
								{buttonText}
							</Button>
						</UniversalLink>
					</div>
				</StyledButtonWrapper>
			</ContentContainer>
		</Wrapper>
	)
}
